import { useState } from 'react';
import { DateTime } from 'luxon';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  ButtonSizeEnum,
  ButtonVariantEnum,
  JxtButton,
  JxtExternalLinkModal,
  generateToast,
  JxtUpdateBookingModal,
} from '@jooxter/ui';
import {
  Booking,
  BookingExternalAction,
  BookingExternalActionTypeEnum,
  isRecurrenceDto,
  RecurrenceDto,
} from '@jooxter/api';
import { FloorQueryKeys } from '@jooxter/floors';
import { useInvalidateWorkplaceQueries } from '@jooxter/workplaces';
import { useUpdateBooking } from '@jooxter/bookings';
import { createGTMUpdateVirtualPathEvent, IBookingForm, ISOToLocalDate, ISOToLocalTime } from '@jooxter/utils';
import {
  BookingQueryKeys,
  ResourceQueryKeys,
  useInvalidateBookingQueries,
  useInvalidateCorporationQueries,
  useInvalidateResourceQuotaQueries,
} from '../../../queries';
import { useBookingActions, useModalViolationsContext } from '../../../hooks';
import { useOffCanvasStore } from '../../../store';
import { useIBookingFormToBookingRequestAdapter } from '../../../adapters/bookings/useIBookingFormToBookingRequestAdapter';
import { IPendingRecurrenceUpdate, IUseBookingSubmission } from './types';

/**
 * Owns everything that happens *after* the form is valid: the create/update mutations, their
 * confirmation modals, cache invalidation and the success/error feedback. The form itself only
 * needs to call `submit`.
 */
export const useBookingSubmission = (
  bookingToEdit?: Booking | null,
  onSuccess?: (id: number) => void
): IUseBookingSubmission => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [alert, setAlert] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingRecurrenceUpdate, setPendingRecurrenceUpdate] = useState<IPendingRecurrenceUpdate>();
  const [showRecurrenceUpdateModal, setShowRecurrenceUpdateModal] = useState(false);
  const [externalLinksToVisit, setExternalLinksToVisit] = useState<BookingExternalAction[]>([]);
  const [showExternalLinksModal, setShowExternalLinksModal] = useState(false);

  const { create, update } = useBookingActions();
  const { mutate: updateRecurrences } = useUpdateBooking(true);
  const [bookingFormToBookingRequestAdapter] = useIBookingFormToBookingRequestAdapter();
  const modalViolationsContext = useModalViolationsContext();
  const { invalidateWorkplaceQueries } = useInvalidateWorkplaceQueries();
  const { invalidateCorporationQueries } = useInvalidateCorporationQueries();
  const { invalidateResourceQuotaQueries } = useInvalidateResourceQuotaQueries();
  const { invalidateBookingQueries } = useInvalidateBookingQueries();
  const { close, openBookingView } = useOffCanvasStore((state) => ({
    close: state.close,
    openBookingView: state.openBookingView,
  }));

  const invalidateResourceAndFloorQueries = () => {
    queryClient.invalidateQueries({ queryKey: [FloorQueryKeys.GetFloorPlanResources] });
    queryClient.invalidateQueries({ queryKey: [ResourceQueryKeys.GetResourceStatus] });
  };

  const getBookingToastSubtitle = (booking: Booking | RecurrenceDto, translationKey: string) => {
    const bookingDate = ISOToLocalDate(booking.start.dateTime, booking.resource.timezone);
    const endDate = ISOToLocalDate(booking.end.dateTime, booking.resource.timezone);
    const isSingleDayBooking = bookingDate === endDate;

    return t<string>(isSingleDayBooking ? translationKey : `${translationKey}-with-end-date`, {
      resourceName: booking.resource.name,
      bookingDate,
      startTime: ISOToLocalTime(booking.start.dateTime, booking.resource.timezone),
      endDate,
      endTime: ISOToLocalTime(booking.end.dateTime, booking.resource.timezone),
    });
  };

  const onSuccessUpdate = (booking: Booking, recurrence: boolean) => {
    invalidateBookingQueries([booking.id]);
    invalidateResourceAndFloorQueries();
    invalidateCorporationQueries();
    invalidateResourceQuotaQueries();
    close();

    generateToast(
      recurrence ? t<string>('booking-and-following-occurrences-updated') : t<string>('update-saved'),
      false,
      getBookingToastSubtitle(booking, 'booking-updated-message')
    );
    onSuccess?.(booking.id);
  };

  const onSuccessCreate = (booking: Booking | RecurrenceDto) => {
    queryClient.setQueryData([BookingQueryKeys.GetBooking, booking.id], booking);
    invalidateBookingQueries([booking.id]);
    invalidateWorkplaceQueries();
    invalidateCorporationQueries();
    invalidateResourceQuotaQueries();
    invalidateResourceAndFloorQueries();

    const bookingOptionsLink = booking.options
      .filter((opt) => opt.link?.href && opt.link.type === BookingExternalActionTypeEnum.Link)
      .map((o) => o.link) as BookingExternalAction[];

    if (bookingOptionsLink.length > 0) {
      setExternalLinksToVisit(bookingOptionsLink);
      setShowExternalLinksModal(true);
    } else {
      close();
    }

    generateToast(
      t<string>('booking-created'),
      false,
      getBookingToastSubtitle(booking, 'booking-created-message'),
      <JxtButton
        className="p-0!"
        size={ButtonSizeEnum.Large}
        variant={ButtonVariantEnum.Link}
        onClick={() => openBookingView(booking.id)}
      >
        {t('open-booking')}
      </JxtButton>
    );

    if (isRecurrenceDto(booking) && booking.violations && Object.keys(booking.violations).length) {
      generateToast(
        t<string>('occurrences.violations.not-created', {
          numberOfViolations: Object.keys(booking.violations).length,
        }),
        true,
        undefined,
        <JxtButton
          className="p-0!"
          size={ButtonSizeEnum.Large}
          variant={ButtonVariantEnum.Link}
          onClick={() => modalViolationsContext?.showViolations(booking)}
        >
          {t('see-details')}
        </JxtButton>
      );
    }
    onSuccess?.(booking.id);
  };

  const onError = (error: unknown) => {
    const axiosError = error as AxiosError;
    const response = axiosError.response?.data as { error: string; description: string }[];

    if (response.length > 0) {
      // ND-6199 (not yet built) is expected to return this code on a quota-overrun 400 -
      // reconcile against the real payload once that ships.
      const isQuotaExceeded = axiosError.response?.status === 400 && response[0].error === 'QUOTA_EXCEEDED';
      setAlert(isQuotaExceeded ? t('booking-form.alert.quota-exceeded') : response[0].error);
    }
  };

  const onSettled = () => setIsSubmitting(false);

  const submit = (data: IBookingForm) => {
    setIsSubmitting(true);
    setAlert(undefined);
    const payload = bookingFormToBookingRequestAdapter(data);

    if (!bookingToEdit) {
      // onError already handles the failure; swallow the rejected promise to avoid an unhandled rejection.
      return create(payload, onSuccessCreate, onError, onSettled).catch(() => {});
    }

    if (bookingToEdit.rrule) {
      setPendingRecurrenceUpdate({ payload, start: data.start.date, end: data.end.date });
      setShowRecurrenceUpdateModal(true);
      createGTMUpdateVirtualPathEvent('/BookingUpdateConfirmation');
      return;
    }

    update({ id: bookingToEdit.id, payload }, (booking) => onSuccessUpdate(booking, false), onError, onSettled);
  };

  const confirmRecurrenceUpdate = (recurrence?: boolean) => {
    if (!bookingToEdit || !pendingRecurrenceUpdate) {
      return;
    }

    const variables = { id: bookingToEdit.id, payload: pendingRecurrenceUpdate.payload };

    if (recurrence) {
      updateRecurrences(variables, {
        onSuccess: (booking: Booking) => onSuccessUpdate(booking, true),
        onError,
        onSettled,
      });
      return;
    }

    update(variables, (booking) => onSuccessUpdate(booking, false), onError, onSettled);
  };

  const modals = (
    <>
      <JxtUpdateBookingModal
        oldEvent={{
          start: bookingToEdit?.start.dateTime ? DateTime.fromISO(bookingToEdit.start.dateTime) : undefined,
          end: bookingToEdit?.end.dateTime ? DateTime.fromISO(bookingToEdit.end.dateTime) : undefined,
        }}
        event={{ start: pendingRecurrenceUpdate?.start, end: pendingRecurrenceUpdate?.end }}
        timezone={bookingToEdit?.resource.timezone}
        recurrence={true}
        show={showRecurrenceUpdateModal}
        onHide={(e) => {
          if (e) {
            confirmRecurrenceUpdate(e.recurrence);
          }
          setShowRecurrenceUpdateModal(false);
          setIsSubmitting(false);
        }}
      />
      <JxtExternalLinkModal
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        externalLinks={externalLinksToVisit.map(({ type, ...rest }) => rest)}
        show={showExternalLinksModal}
        onHide={() => {
          setShowExternalLinksModal(false);
          close();
        }}
      />
    </>
  );

  return { submit, alert, clearAlert: () => setAlert(undefined), onError, isSubmitting, setIsSubmitting, modals };
};
